import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Text,
  Flex,
  Icon,
  useColorModeValue,
  Progress,
  HStack,
  VStack,
  Badge,
  Divider,
  Skeleton,
  useToken,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Checkbox,
  Select,
} from '@chakra-ui/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faEye,
  faMousePointer,
  faPercent,
  faArrowTrendUp,
  faArrowTrendDown,
  faBolt,
  faChartPie,
  faGlobe,
  faEnvelope,
  faUserPlus,
  faHandPointer,
  faChartLine,
  faClock,
  faDesktop,
  faMobileAlt,
  faTabletAlt,
  faLink,
  faSearch,
  faShareNodes,
  faArrowRightToBracket,
  faDownload,
  faCog,
} from '@fortawesome/free-solid-svg-icons';
import { ExportBuilderModal } from '../components/exports/ExportBuilderModal';
import { useRecoilValue, useRecoilState } from 'recoil';
import { selectedSiteAtom } from '../state/sitesAtom';
import { dateRangeAtom } from '../state/dateRangeAtom';
import { botFilterAtom } from '../state/botFilterAtom';
import { useAnalyticsOverview, useTrafficData, useTrafficSources, useDevices } from '../hooks/useAnalytics';
import { useCampaigns } from '../hooks/useCampaigns';
import { parseDateRange } from '../hooks/useAnalytics';
import { useChartAnimation } from '../hooks/useChartAnimation';
import { formatNumber, formatPercent, formatShare } from '../utils/format';
import { CHART_PALETTE, useChartColors, ChartPieTooltip } from '../components/common/chartTheme';
import DateRangePicker from '../components/common/DateRangePicker';

// Dashboard Configuration Constants
const DASHBOARD_CONFIG_KEY = 'statfalcon_dashboard_config';

const DEFAULT_CONFIG = {
  version: 1,
  preset: 'all',
  cards: {
    uniqueVisitors: true,
    pageViews: true,
    adImpressions: true,
    avgCtr: true,
    avgSession: true,
    avgTimeOnPage: true,
    trafficTrend: true,
    quickInsights: true,
    deviceBreakdown: true,
    topReferrers: true,
    channelPerformance: true,
    topCampaigns: true,
    recentActivity: true,
  },
};

const PRESETS = {
  all: {
    uniqueVisitors: true,
    pageViews: true,
    adImpressions: true,
    avgCtr: true,
    avgSession: true,
    avgTimeOnPage: true,
    trafficTrend: true,
    quickInsights: true,
    deviceBreakdown: true,
    topReferrers: true,
    channelPerformance: true,
    topCampaigns: true,
    recentActivity: true,
  },
  webstats: {
    uniqueVisitors: true,
    pageViews: true,
    adImpressions: false,
    avgCtr: false,
    avgSession: true,
    avgTimeOnPage: true,
    trafficTrend: true,
    quickInsights: true,
    deviceBreakdown: true,
    topReferrers: true,
    channelPerformance: false,
    topCampaigns: false,
    recentActivity: true,
  },
  ads: {
    uniqueVisitors: true,
    pageViews: false,
    adImpressions: true,
    avgCtr: true,
    avgSession: false,
    avgTimeOnPage: false,
    trafficTrend: false,
    quickInsights: false,
    deviceBreakdown: false,
    topReferrers: false,
    channelPerformance: true,
    topCampaigns: true,
    recentActivity: true,
  },
};

const CARD_LABELS = {
  uniqueVisitors: 'Unique Visitors',
  pageViews: 'Page Views',
  adImpressions: 'Ad Impressions',
  avgCtr: 'Avg CTR',
  avgSession: 'Avg Session',
  avgTimeOnPage: 'Avg Time on Page',
  trafficTrend: 'Traffic Trend',
  quickInsights: 'Quick Insights',
  deviceBreakdown: 'Device Breakdown',
  topReferrers: 'Top Referrers',
  channelPerformance: 'Channel Performance',
  topCampaigns: 'Top Campaigns',
  recentActivity: 'Recent Activity',
};

const CARD_GROUPS = {
  stats: ['uniqueVisitors', 'pageViews', 'adImpressions', 'avgCtr', 'avgSession', 'avgTimeOnPage'],
  analytics: ['trafficTrend', 'quickInsights', 'deviceBreakdown', 'topReferrers'],
  advertising: ['channelPerformance', 'topCampaigns', 'recentActivity'],
};

const loadConfig = () => {
  try {
    const saved = localStorage.getItem(DASHBOARD_CONFIG_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...DEFAULT_CONFIG, ...parsed, cards: { ...DEFAULT_CONFIG.cards, ...parsed.cards } };
    }
  } catch (e) {
    console.error('Failed to load dashboard config:', e);
  }
  return DEFAULT_CONFIG;
};

const saveConfig = (config) => {
  try {
    localStorage.setItem(DASHBOARD_CONFIG_KEY, JSON.stringify(config));
  } catch (e) {
    console.error('Failed to save dashboard config:', e);
  }
};

function StatCard({ label, value, change, changeType, icon, loading }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');

  const configs = {
    'Unique Visitors': {
      iconColor: '#6366f1',
      gradient: 'linear(135deg, #6366f1 0%, #8b5cf6 100%)',
      lightBg: 'linear(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
      darkBg: 'linear(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
    },
    'Page Views': {
      iconColor: '#06b6d4',
      gradient: 'linear(135deg, #06b6d4 0%, #3b82f6 100%)',
      lightBg: 'linear(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
      darkBg: 'linear(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)',
    },
    'Ad Impressions': {
      iconColor: '#f59e0b',
      gradient: 'linear(135deg, #f59e0b 0%, #ef4444 100%)',
      lightBg: 'linear(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(239, 68, 68, 0.1) 100%)',
      darkBg: 'linear(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(239, 68, 68, 0.2) 100%)',
    },
    'Avg CTR': {
      iconColor: '#10b981',
      gradient: 'linear(135deg, #10b981 0%, #059669 100%)',
      lightBg: 'linear(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
      darkBg: 'linear(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)',
    },
    'Avg Session': {
      iconColor: '#8b5cf6',
      gradient: 'linear(135deg, #8b5cf6 0%, #d946ef 100%)',
      lightBg: 'linear(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(217, 70, 239, 0.1) 100%)',
      darkBg: 'linear(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(217, 70, 239, 0.2) 100%)',
    },
    'Avg Time on Page': {
      iconColor: '#f59e0b',
      gradient: 'linear(135deg, #f59e0b 0%, #ef4444 100%)',
      lightBg: 'linear(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(239, 68, 68, 0.1) 100%)',
      darkBg: 'linear(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(239, 68, 68, 0.2) 100%)',
    },
  };

  const config = configs[label] || configs['Unique Visitors'];
  const bgGradient = useColorModeValue(config.lightBg, config.darkBg);

  return (
    <Card
      position="relative"
      overflow="hidden"
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      _hover={{ transform: 'translateY(-1px)' }}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        h="3px"
        bgGradient={config.gradient}
      />
      <CardBody pt={6}>
        <Flex justify="space-between" align="flex-start">
          <Box flex={1}>
            <Text
              fontSize="xs"
              fontWeight="500"
              color={textSecondary}
              textTransform="uppercase"
              letterSpacing="wide"
              mb={1}
            >
              {label}
            </Text>
            {loading ? (
              <Skeleton height="36px" width="100px" mb={2} />
            ) : (
              <Text
                fontSize={{ base: '2xl', md: '3xl' }}
                fontWeight="700"
                bgGradient={config.gradient}
                bgClip="text"
                lineHeight="1.2"
              >
                {value}
              </Text>
            )}
            {loading ? (
              <Skeleton height="20px" width="120px" />
            ) : change ? (
              <HStack mt={2} spacing={1}>
                <Box
                  color={changeType === 'increase' ? 'green.400' : 'red.400'}
                  fontSize="xs"
                >
                  <FontAwesomeIcon icon={changeType === 'increase' ? faArrowTrendUp : faArrowTrendDown} />
                </Box>
                <Text
                  fontSize="sm"
                  fontWeight="600"
                  color={changeType === 'increase' ? 'green.400' : 'red.400'}
                >
                  {change}
                </Text>
                <Text fontSize="sm" color={textSecondary}>
                  vs last period
                </Text>
              </HStack>
            ) : null}
          </Box>
          <Flex
            align="center"
            justify="center"
            w={{ base: '40px', md: '44px' }}
            h={{ base: '40px', md: '44px' }}
            bgGradient={bgGradient}
            borderRadius="xl"
            color={config.iconColor}
            fontSize="md"
          >
            <FontAwesomeIcon icon={icon} />
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
}

function TrafficTrendCard({ data, loading, dateRange }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const [brandColor, accentColor, cyanColor] = useToken('colors', ['brand.500', 'accent.500', 'cyan.500']);
  const anim = useChartAnimation(loading);

  const { tooltipBg, tooltipText, gridColor, axisTextColor } = useChartColors();

  const [activeMetrics, setActiveMetrics] = useState(['visitors', 'pageviews', 'visits']);
  const toggleMetric = (metric) => {
    setActiveMetrics(prev =>
      prev.includes(metric) ? prev.filter(m => m !== metric) : [...prev, metric]
    );
  };

  const preset = typeof dateRange === 'string' ? dateRange : dateRange?.preset;
  const isHourly = preset === 'today' || preset === 'yesterday';

  const chartData = data?.map(item => {
    const d = new Date(item.date);
    return {
      date: isHourly
        ? d.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true })
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      visitors: item.visitors || 0,
      pageviews: item.pageviews || 0,
      visits: item.visits || 0,
    };
  }) || [];

  const totalVisitors = chartData.reduce((sum, item) => sum + item.visitors, 0);
  const totalPageviews = chartData.reduce((sum, item) => sum + item.pageviews, 0);
  const totalSessions = chartData.reduce((sum, item) => sum + item.visits, 0);

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center">
          <HStack spacing={2}>
            <Box
              p={2}
              bgGradient="linear(135deg, brand.500 0%, cyan.500 100%)"
              borderRadius="lg"
            >
              <Icon color="white" boxSize={4}>
                <FontAwesomeIcon icon={faChartLine} />
              </Icon>
            </Box>
            <Heading size="md">Traffic Trend</Heading>
          </HStack>
          <HStack spacing={4}>
            <VStack spacing={0} align="flex-end">
              <Text fontSize="xs" color={textSecondary}>Visitors</Text>
              <Text fontSize="sm" fontWeight="600" color="brand.500">{formatNumber(totalVisitors)}</Text>
            </VStack>
            <VStack spacing={0} align="flex-end">
              <Text fontSize="xs" color={textSecondary}>Pageviews</Text>
              <Text fontSize="sm" fontWeight="600" color="accent.500">{formatNumber(totalPageviews)}</Text>
            </VStack>
            <VStack spacing={0} align="flex-end">
              <Text fontSize="xs" color={textSecondary}>Sessions</Text>
              <Text fontSize="sm" fontWeight="600" color="cyan.500">{formatNumber(totalSessions)}</Text>
            </VStack>
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody pt={0}>
        {loading ? (
          <Skeleton height="300px" borderRadius="lg" />
        ) : chartData.length > 0 ? (
          <Box>
            <HStack spacing={2} mb={4} flexWrap="wrap">
              <Text fontSize="sm" color={textSecondary} mr={2}>Show:</Text>
              <Button
                size="sm"
                variant={activeMetrics.includes('visitors') ? 'solid' : 'outline'}
                colorScheme="brand"
                borderRadius="full"
                onClick={() => toggleMetric('visitors')}
                bgGradient={activeMetrics.includes('visitors') ? 'linear(to-r, brand.500, accent.500)' : undefined}
                _hover={{ bgGradient: activeMetrics.includes('visitors') ? 'linear(to-r, brand.600, accent.600)' : undefined }}
                leftIcon={<Box w={2} h={2} borderRadius="full" bg={activeMetrics.includes('visitors') ? 'white' : 'brand.500'} />}
              >
                Visitors
              </Button>
              <Button
                size="sm"
                variant={activeMetrics.includes('pageviews') ? 'solid' : 'outline'}
                colorScheme="pink"
                borderRadius="full"
                onClick={() => toggleMetric('pageviews')}
                bgGradient={activeMetrics.includes('pageviews') ? 'linear(to-r, pink.500, purple.500)' : undefined}
                _hover={{ bgGradient: activeMetrics.includes('pageviews') ? 'linear(to-r, pink.600, purple.600)' : undefined }}
                leftIcon={<Box w={2} h={2} borderRadius="full" bg={activeMetrics.includes('pageviews') ? 'white' : 'pink.500'} />}
              >
                Pageviews
              </Button>
              <Button
                size="sm"
                variant={activeMetrics.includes('visits') ? 'solid' : 'outline'}
                colorScheme="cyan"
                borderRadius="full"
                onClick={() => toggleMetric('visits')}
                bgGradient={activeMetrics.includes('visits') ? 'linear(to-r, cyan.500, blue.500)' : undefined}
                _hover={{ bgGradient: activeMetrics.includes('visits') ? 'linear(to-r, cyan.600, blue.600)' : undefined }}
                leftIcon={<Box w={2} h={2} borderRadius="full" bg={activeMetrics.includes('visits') ? 'white' : 'cyan.500'} />}
              >
                Sessions
              </Button>
            </HStack>
            <Box h="300px">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={brandColor} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={brandColor} stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorPageviews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={accentColor} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={accentColor} stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={cyanColor} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={cyanColor} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: axisTextColor }}
                    interval="preserveStartEnd"
                  />
                  <YAxis hide />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: tooltipBg,
                      border: `1px solid ${gridColor}`,
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: tooltipText,
                    }}
                    labelStyle={{ color: tooltipText }}
                  />
                  {activeMetrics.includes('visitors') && (
                    <Area
                      {...anim}
                      type="monotone"
                      dataKey="visitors"
                      stroke={brandColor}
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorVisitors)"
                      name="Visitors"
                    />
                  )}
                  {activeMetrics.includes('pageviews') && (
                    <Area
                      {...anim}
                      type="monotone"
                      dataKey="pageviews"
                      stroke={accentColor}
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorPageviews)"
                      name="Pageviews"
                    />
                  )}
                  {activeMetrics.includes('visits') && (
                    <Area
                      {...anim}
                      type="monotone"
                      dataKey="visits"
                      stroke={cyanColor}
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorVisits)"
                      name="Sessions"
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        ) : (
          <Text color={textSecondary} fontSize="sm" textAlign="center" py={8}>
            No traffic data available
          </Text>
        )}
      </CardBody>
    </Card>
  );
}

function DeviceBreakdownCard({ data, loading }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const anim = useChartAnimation(loading);

  const deviceIcons = {
    desktop: faDesktop,
    mobile: faMobileAlt,
    tablet: faTabletAlt,
  };

  const chartData = data?.map((device, index) => {
    const deviceType = device.device || device.device_type || 'desktop';
    return {
      name: deviceType.charAt(0).toUpperCase() + deviceType.slice(1),
      value: device.visitors || 0,
      percentage: device.percentage || 0,
      type: deviceType,
      fill: CHART_PALETTE[index % CHART_PALETTE.length],
    };
  }) || [];

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
      <CardHeader pb={2}>
        <HStack spacing={2}>
          <Box
            p={2}
            bgGradient="linear(135deg, purple.500 0%, pink.500 100%)"
            borderRadius="lg"
          >
            <Icon color="white" boxSize={4}>
              <FontAwesomeIcon icon={faChartPie} />
            </Icon>
          </Box>
          <Heading size="md">Device Breakdown</Heading>
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        {loading ? (
          <Skeleton height="120px" borderRadius="lg" />
        ) : chartData.length > 0 && total > 0 ? (
          <Flex align="center" justify="space-between">
            <Box w="120px" h="120px">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    {...anim}
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={55}
                    paddingAngle={2}
                    dataKey="value"
                  />
                  <RechartsTooltip content={<ChartPieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <VStack spacing={2} align="stretch" flex={1} ml={4}>
              {chartData.map((device, index) => (
                <Flex key={device.name} justify="space-between" align="center">
                  <HStack spacing={2}>
                    <Box
                      w="8px"
                      h="8px"
                      borderRadius="full"
                      bg={CHART_PALETTE[index % CHART_PALETTE.length]}
                    />
                    <Icon color={textSecondary} boxSize={3}>
                      <FontAwesomeIcon icon={deviceIcons[device.type] || faDesktop} />
                    </Icon>
                    <Text fontSize="sm">{device.name}</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Text fontSize="sm" fontWeight="600">{formatShare(device.percentage, device.value)}</Text>
                  </HStack>
                </Flex>
              ))}
            </VStack>
          </Flex>
        ) : (
          <Text color={textSecondary} fontSize="sm" textAlign="center" py={8}>
            No device data available
          </Text>
        )}
      </CardBody>
    </Card>
  );
}

function TopReferrersCard({ data, loading }) {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const sourceIcons = {
    direct: faArrowRightToBracket,
    search: faSearch,
    social: faShareNodes,
    referral: faLink,
    email: faEnvelope,
  };

  const sourceColors = {
    direct: 'green',
    search: 'orange',
    social: 'purple',
    referral: 'blue',
    email: 'cyan',
  };

  const topSources = data?.slice(0, 5) || [];

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center">
          <HStack spacing={2}>
            <Box
              p={2}
              bgGradient="linear(135deg, orange.500 0%, red.500 100%)"
              borderRadius="lg"
            >
              <Icon color="white" boxSize={4}>
                <FontAwesomeIcon icon={faGlobe} />
              </Icon>
            </Box>
            <Heading size="md">Top Referrers</Heading>
          </HStack>
          <Text
            fontSize="sm"
            color="brand.500"
            fontWeight="500"
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={() => navigate('/analytics/sources')}
          >
            View all
          </Text>
        </Flex>
      </CardHeader>
      <CardBody pt={0}>
        {loading ? (
          <VStack spacing={3}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} height="36px" borderRadius="lg" />
            ))}
          </VStack>
        ) : topSources.length > 0 ? (
          <VStack spacing={0} align="stretch" divider={<Divider borderColor={borderColor} />}>
            {topSources.map((source, index) => {
              const color = sourceColors[source.type] || 'gray';
              const icon = sourceIcons[source.type] || faGlobe;
              return (
                <Flex
                  key={index}
                  justify="space-between"
                  align="center"
                  py={2}
                  px={2}
                  mx={-2}
                  borderRadius="lg"
                  transition="all 0.2s"
                  _hover={{ bg: hoverBg }}
                >
                  <HStack spacing={3}>
                    <Box
                      p={1.5}
                      bg={`${color}.100`}
                      borderRadius="md"
                    >
                      <Icon color={`${color}.500`} boxSize={3}>
                        <FontAwesomeIcon icon={icon} />
                      </Icon>
                    </Box>
                    <Text fontSize="sm" fontWeight="500">
                      {source.name || 'Direct'}
                    </Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Text fontSize="sm" color={textSecondary}>
                      {formatNumber(source.visitors)} visitors
                    </Text>
                    <Badge colorScheme={color} variant="subtle" borderRadius="full" fontSize="xs">
                      {formatShare(source.percentage, source.visitors)}
                    </Badge>
                  </HStack>
                </Flex>
              );
            })}
          </VStack>
        ) : (
          <Text color={textSecondary} fontSize="sm" textAlign="center" py={4}>
            No referrer data available
          </Text>
        )}
      </CardBody>
    </Card>
  );
}

function QuickInsightsCard({ data, loading }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const progressBg = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');

  // Calculate insights from real data
  const totalVisitors = (data?.uniqueVisitors || 0);
  const totalPageviews = (data?.totalPageviews || 0);
  const bounceRate = data?.bounceRate || 0;
  const engagementRate = data?.engagementRate || 0;
  const avgSessionDuration = data?.avgSessionDuration || 0;

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  const insights = [
    { label: 'Bounce Rate', value: Math.min(bounceRate, 100), color: bounceRate > 50 ? 'red' : 'green' },
    { label: 'Engagement Rate', value: Math.min(engagementRate, 100), color: 'purple', rawValue: `${engagementRate}%` },
    { label: 'Pages/Session', value: Math.min((data?.pagesPerSession || 0) * 20, 100), color: 'cyan', rawValue: formatPercent(data?.pagesPerSession || 0, 1).replace('%', '') },
    { label: 'Avg Session', value: Math.min((avgSessionDuration / 300) * 100, 100), color: 'orange', rawValue: formatDuration(avgSessionDuration) },
  ];

  const hasData = totalVisitors > 0 || totalPageviews > 0;

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center">
          <HStack spacing={2}>
            <Box
              p={2}
              bgGradient="linear(135deg, brand.500 0%, purple.500 100%)"
              borderRadius="lg"
            >
              <Icon color="white" boxSize={4}>
                <FontAwesomeIcon icon={faBolt} />
              </Icon>
            </Box>
            <Heading size="md">Quick Insights</Heading>
          </HStack>
          {hasData && (
            <Badge colorScheme="green" variant="subtle" borderRadius="full">
              Live
            </Badge>
          )}
        </Flex>
      </CardHeader>
      <CardBody>
        {loading ? (
          <VStack spacing={4} align="stretch">
            {[1, 2, 3, 4].map((i) => (
              <Box key={i}>
                <Skeleton height="16px" width="60%" mb={2} />
                <Skeleton height="8px" borderRadius="full" />
              </Box>
            ))}
          </VStack>
        ) : hasData ? (
          <VStack spacing={4} align="stretch">
            {insights.map((insight) => (
              <Box key={insight.label}>
                <Flex justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="500">
                    {insight.label}
                  </Text>
                  <Text fontSize="sm" fontWeight="600" color={`${insight.color}.500`}>
                    {insight.rawValue || `${Math.round(insight.value)}%`}
                  </Text>
                </Flex>
                <Progress
                  value={insight.value}
                  size="sm"
                  colorScheme={insight.color}
                  borderRadius="full"
                  bg={progressBg}
                />
              </Box>
            ))}
          </VStack>
        ) : (
          <Text color={textSecondary} fontSize="sm" textAlign="center" py={4}>
            No analytics data yet. Visit your tracked site to generate data.
          </Text>
        )}
      </CardBody>
    </Card>
  );
}

function ChannelPerformanceCard({ webImpressions, webClicks, emailImpressions, emailClicks, loading }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const itemBg = useColorModeValue('gray.50', 'gray.700');

  const total = (webImpressions || 0) + (emailImpressions || 0);
  const webPercentage = total > 0 ? Math.round(((webImpressions || 0) / total) * 100) : 0;
  const emailPercentage = total > 0 ? Math.round(((emailImpressions || 0) / total) * 100) : 0;

  const calculateCTR = (impressions, clicks) => {
    if (!impressions || impressions === 0) return '0';
    const ctr = (clicks / impressions) * 100;
    return parseFloat(ctr.toFixed(2)).toString();
  };

  const channels = [
    {
      name: 'Website',
      icon: faGlobe,
      impressions: formatNumber(webImpressions || 0),
      clicks: formatNumber(webClicks || 0),
      ctr: calculateCTR(webImpressions, webClicks),
      color: 'blue',
      percentage: webPercentage,
    },
    {
      name: 'Email',
      icon: faEnvelope,
      impressions: formatNumber(emailImpressions || 0),
      clicks: formatNumber(emailClicks || 0),
      ctr: calculateCTR(emailImpressions, emailClicks),
      color: 'purple',
      percentage: emailPercentage,
    },
  ];

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
      <CardHeader pb={2}>
        <HStack spacing={2}>
          <Box
            p={2}
            bgGradient="linear(135deg, cyan.500 0%, blue.500 100%)"
            borderRadius="lg"
          >
            <Icon color="white" boxSize={4}>
              <FontAwesomeIcon icon={faChartPie} />
            </Icon>
          </Box>
          <Heading size="md">Channel Performance</Heading>
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {channels.map((channel) => (
            <Box
              key={channel.name}
              p={4}
              bg={itemBg}
              borderRadius="xl"
              transition="all 0.2s"
              _hover={{ transform: 'scale(1.02)' }}
            >
              <Flex justify="space-between" align="center" mb={3}>
                <HStack spacing={3}>
                  <Box
                    p={2}
                    bg={`${channel.color}.100`}
                    borderRadius="lg"
                  >
                    <Icon color={`${channel.color}.500`} boxSize={4}>
                      <FontAwesomeIcon icon={channel.icon} />
                    </Icon>
                  </Box>
                  <Box>
                    <Text fontWeight="600">{channel.name}</Text>
                    <Text fontSize="xs" color={textSecondary}>
                      {channel.percentage}% of total
                    </Text>
                  </Box>
                </HStack>
                <Badge colorScheme={channel.color} variant="subtle" borderRadius="full">
                  {channel.ctr}% CTR
                </Badge>
              </Flex>
              <SimpleGrid columns={2} spacing={2} fontSize="sm">
                <Box>
                  <Text color={textSecondary} fontSize="xs">Impressions</Text>
                  {loading ? (
                    <Skeleton height="20px" width="60px" />
                  ) : (
                    <Text fontWeight="600">{channel.impressions}</Text>
                  )}
                </Box>
                <Box>
                  <Text color={textSecondary} fontSize="xs">Clicks</Text>
                  {loading ? (
                    <Skeleton height="20px" width="60px" />
                  ) : (
                    <Text fontWeight="600">{channel.clicks}</Text>
                  )}
                </Box>
              </SimpleGrid>
            </Box>
          ))}
        </VStack>
      </CardBody>
    </Card>
  );
}

function TopCampaignsCard({ dateRange }) {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const itemHoverBg = useColorModeValue('gray.50', 'gray.700');

  const filters = useMemo(() => {
    if (!dateRange) return undefined;
    const { start, end } = parseDateRange(dateRange);
    return { start: start.toISOString(), end: end.toISOString() };
  }, [dateRange]);

  const { data: campaigns, loading } = useCampaigns(filters);


  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center">
          <Heading size="md">Top Campaigns</Heading>
          <Text
            fontSize="sm"
            color="brand.500"
            fontWeight="500"
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={() => navigate('/campaigns')}
          >
            View all
          </Text>
        </Flex>
      </CardHeader>
      <CardBody>
        {loading ? (
          <VStack spacing={3}>
            <Skeleton height="40px" />
            <Skeleton height="40px" />
            <Skeleton height="40px" />
          </VStack>
        ) : campaigns && campaigns.length > 0 ? (
          <VStack spacing={0} align="stretch" divider={<Divider borderColor={borderColor} />}>
            {campaigns.slice(0, 4).map((campaign, index) => (
              <Flex
                key={campaign.campaign_id}
                justify="space-between"
                align="center"
                py={3}
                px={2}
                mx={-2}
                borderRadius="lg"
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ bg: itemHoverBg }}
                onClick={() => navigate(`/campaigns?campaign=${campaign.campaign_id}`)}
              >
                <HStack spacing={3}>
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    color={textSecondary}
                    w="20px"
                  >
                    {index + 1}
                  </Text>
                  <Box>
                    <Text
                      fontWeight="500"
                      fontSize="sm"
                      color="brand.500"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      {campaign.campaign_name}
                    </Text>
                    <Text fontSize="xs" color={textSecondary}>
                      {formatNumber(campaign.impressions || 0)} impressions
                    </Text>
                  </Box>
                </HStack>
                <HStack spacing={2}>
                  <Badge
                    colorScheme={campaign.status === 'active' ? 'green' : 'yellow'}
                    variant="subtle"
                    borderRadius="full"
                    fontSize="xs"
                  >
                    {formatPercent(campaign.ctr || 0, 2)}
                  </Badge>
                </HStack>
              </Flex>
            ))}
          </VStack>
        ) : (
          <Text color={textSecondary} fontSize="sm" textAlign="center" py={4}>
            No campaigns yet
          </Text>
        )}
      </CardBody>
    </Card>
  );
}

function RecentActivityCard() {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const selectedSite = useRecoilValue(selectedSiteAtom);

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        const serverUrl = window.location.origin.replace(':3058', ':3059');
        const token = localStorage.getItem('wr_token');
        const siteParam = selectedSite && selectedSite !== 'all' ? `&siteId=${selectedSite}` : '';
        const response = await fetch(`${serverUrl}/api/analytics/recent-activity?limit=6${siteParam}`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
        const data = await response.json();
        setActivities(data.data || []);
      } catch (error) {
        console.error('Error fetching recent activity:', error);
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
    // Refresh every 30 seconds
    const interval = setInterval(fetchActivities, 30000);
    return () => clearInterval(interval);
  }, [selectedSite]);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'pageview': return faEye;
      case 'click': return faHandPointer;
      case 'visitor': return faUserPlus;
      default: return faEye;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'pageview': return 'blue';
      case 'click': return 'green';
      case 'visitor': return 'purple';
      default: return 'gray';
    }
  };

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center">
          <Heading size="md">Recent Activity</Heading>
          {activities.length > 0 && (
            <Badge colorScheme="green" variant="subtle" borderRadius="full">
              Live
            </Badge>
          )}
        </Flex>
      </CardHeader>
      <CardBody>
        {loading ? (
          <VStack spacing={3} align="stretch">
            {[1, 2, 3, 4].map((i) => (
              <HStack key={i} spacing={3}>
                <Skeleton borderRadius="full" width="32px" height="32px" />
                <Box flex={1}>
                  <Skeleton height="14px" width="80%" mb={1} />
                  <Skeleton height="12px" width="40%" />
                </Box>
              </HStack>
            ))}
          </VStack>
        ) : activities.length > 0 ? (
          <VStack spacing={3} align="stretch">
            {activities.map((activity, index) => (
              <HStack key={index} spacing={3}>
                <Flex
                  align="center"
                  justify="center"
                  w="32px"
                  h="32px"
                  bg={`${getActivityColor(activity.type)}.100`}
                  borderRadius="full"
                >
                  <Icon
                    color={`${getActivityColor(activity.type)}.500`}
                    boxSize={3}
                  >
                    <FontAwesomeIcon icon={getActivityIcon(activity.type)} />
                  </Icon>
                </Flex>
                <Box flex={1} minW={0}>
                  <Text fontSize="sm" noOfLines={1}>
                    <Text as="span" fontWeight="600">
                      {activity.visitor}
                    </Text>
                    {' '}{activity.action}{' '}
                    <Text as="span" fontWeight="500" color="brand.500">
                      {activity.item}
                    </Text>
                  </Text>
                  <Text fontSize="xs" color={textSecondary}>
                    {activity.timeFormatted}
                    {activity.site && ` on ${activity.site}`}
                  </Text>
                </Box>
              </HStack>
            ))}
          </VStack>
        ) : (
          <Text color={textSecondary} fontSize="sm" textAlign="center" py={4}>
            No recent activity. Visit your tracked site to generate data.
          </Text>
        )}
      </CardBody>
    </Card>
  );
}

function DashboardConfigModal({ isOpen, onClose, config, onConfigChange }) {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const sectionBg = useColorModeValue('gray.50', 'gray.700');
  const textSecondary = useColorModeValue('gray.500', 'gray.400');

  const handlePresetChange = (e) => {
    const preset = e.target.value;
    const newCards = preset === 'custom' ? config.cards : { ...PRESETS[preset] };
    const newConfig = { ...config, preset, cards: newCards };
    onConfigChange(newConfig);
    saveConfig(newConfig);
  };

  const handleCardToggle = (cardKey) => {
    const newCards = { ...config.cards, [cardKey]: !config.cards[cardKey] };
    const newConfig = { ...config, preset: 'custom', cards: newCards };
    onConfigChange(newConfig);
    saveConfig(newConfig);
  };

  const handleReset = () => {
    onConfigChange(DEFAULT_CONFIG);
    saveConfig(DEFAULT_CONFIG);
  };

  const renderCardGroup = (groupName, cardKeys, label) => (
    <Box key={groupName} mb={4}>
      <Text fontSize="xs" fontWeight="600" color={textSecondary} textTransform="uppercase" mb={2}>
        {label}
      </Text>
      <SimpleGrid columns={2} spacing={2}>
        {cardKeys.map((key) => (
          <Checkbox
            key={key}
            isChecked={config.cards[key]}
            onChange={() => handleCardToggle(key)}
            size="sm"
          >
            {CARD_LABELS[key]}
          </Checkbox>
        ))}
      </SimpleGrid>
    </Box>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Dashboard Settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box mb={4}>
            <Text fontSize="sm" fontWeight="500" mb={2}>
              Preset
            </Text>
            <Select value={config.preset} onChange={handlePresetChange} size="sm">
              <option value="all">All Cards</option>
              <option value="webstats">WebStats Focus</option>
              <option value="ads">Ads Focus</option>
              <option value="custom">Custom</option>
            </Select>
          </Box>

          <Divider mb={4} borderColor={borderColor} />

          <Box bg={sectionBg} p={4} borderRadius="lg">
            {renderCardGroup('stats', CARD_GROUPS.stats, 'Quick Stats')}
            {renderCardGroup('analytics', CARD_GROUPS.analytics, 'Analytics')}
            {renderCardGroup('advertising', CARD_GROUPS.advertising, 'Advertising')}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={handleReset} mr={3}>
            Reset to Defaults
          </Button>
          <Button colorScheme="brand" size="sm" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function DashboardPage() {
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const [dateRange, setDateRange] = useRecoilState(dateRangeAtom);
  const filterBots = useRecoilValue(botFilterAtom);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [dashboardConfig, setDashboardConfig] = useState(loadConfig);
  const { isOpen: isExportOpen, onOpen: onExportOpen, onClose: onExportClose } = useDisclosure();
  const { isOpen: isConfigOpen, onOpen: onConfigOpen, onClose: onConfigClose } = useDisclosure();

  const { data, comparisonData, loading } = useAnalyticsOverview(dateRange, compareEnabled, filterBots);
  const { data: trafficData, loading: trafficLoading } = useTrafficData(dateRange, 'day', false, filterBots);
  const { data: sourcesData, loading: sourcesLoading } = useTrafficSources(dateRange);
  const { data: devicesData, loading: devicesLoading } = useDevices(dateRange);

  // Format duration for display
  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  // Calculate percentage changes when comparison is enabled
  const getChange = (current, previous) => {
    if (!compareEnabled || !previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return {
      value: formatPercent(Math.abs(change), 1),
      type: change >= 0 ? 'increase' : 'decrease',
    };
  };

  const visitorChange = getChange(data?.uniqueVisitors, comparisonData?.uniqueVisitors);
  const pageviewChange = getChange(data?.totalPageviews, comparisonData?.totalPageviews);
  const impressionChange = getChange(data?.adImpressions, comparisonData?.adImpressions);
  const ctrChange = getChange(data?.avgCtr, comparisonData?.avgCtr);
  const sessionChange = getChange(data?.avgSessionDuration, comparisonData?.avgSessionDuration);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  return (
    <Box>
      {/* Page Header */}
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        mb={{ base: 6, md: 8 }}
        gap={4}
      >
        <Box>
          <Heading
            size={{ base: 'lg', md: 'xl' }}
            bgGradient="linear(to-r, brand.500, purple.500)"
            bgClip="text"
            fontWeight="700"
          >
            Dashboard
          </Heading>
          <Text color={textSecondary} mt={1}>
            Welcome back! Here's what's happening with your ads.
          </Text>
        </Box>
        <Flex direction={{ base: 'column', lg: 'row' }} gap={3} align={{ base: 'flex-start', lg: 'center' }}>
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            showCompare={true}
            compareEnabled={compareEnabled}
            onCompareChange={setCompareEnabled}
            size="sm"
          />
          <HStack spacing={2}>
            <Button
              leftIcon={<FontAwesomeIcon icon={faCog} />}
              size="sm"
              variant="outline"
              borderRadius="xl"
              onClick={onConfigOpen}
            >
              Customize
            </Button>
            <Button
              leftIcon={<FontAwesomeIcon icon={faDownload} />}
              size="sm"
              variant="outline"
              borderRadius="xl"
              onClick={onExportOpen}
            >
              Export
            </Button>
          </HStack>
        </Flex>
      </Flex>

      {/* Quick Stats */}
      {(() => {
        const visibleStats = CARD_GROUPS.stats.filter((k) => dashboardConfig.cards[k]);
        if (visibleStats.length === 0) return null;
        const colCount = Math.min(visibleStats.length, 6);
        return (
          <SimpleGrid
            columns={{ base: 1, sm: Math.min(2, colCount), lg: colCount }}
            spacing={{ base: 4, md: 6 }}
            mb={{ base: 6, md: 8 }}
          >
            {dashboardConfig.cards.uniqueVisitors && (
              <StatCard
                label="Unique Visitors"
                value={formatNumber(data?.uniqueVisitors)}
                change={visitorChange?.value}
                changeType={visitorChange?.type}
                icon={faUsers}
                loading={loading}
              />
            )}
            {dashboardConfig.cards.pageViews && (
              <StatCard
                label="Page Views"
                value={formatNumber(data?.totalPageviews)}
                change={pageviewChange?.value}
                changeType={pageviewChange?.type}
                icon={faEye}
                loading={loading}
              />
            )}
            {dashboardConfig.cards.adImpressions && (
              <StatCard
                label="Ad Impressions"
                value={formatNumber(data?.adImpressions)}
                change={impressionChange?.value}
                changeType={impressionChange?.type}
                icon={faMousePointer}
                loading={loading}
              />
            )}
            {dashboardConfig.cards.avgCtr && (
              <StatCard
                label="Avg CTR"
                value={formatPercent(data?.ctr, 2, true)}
                change={ctrChange?.value}
                changeType={ctrChange?.type}
                icon={faPercent}
                loading={loading}
              />
            )}
            {dashboardConfig.cards.avgSession && (
              <StatCard
                label="Avg Session"
                value={formatDuration(data?.avgSessionDuration)}
                change={sessionChange?.value}
                changeType={sessionChange?.type}
                icon={faClock}
                loading={loading}
              />
            )}
            {dashboardConfig.cards.avgTimeOnPage && (
              <StatCard
                label="Avg Time on Page"
                value={formatDuration(data?.avgTimeOnPage)}
                icon={faClock}
                loading={loading}
              />
            )}
          </SimpleGrid>
        );
      })()}

      {/* Traffic Trend - Full Width */}
      {dashboardConfig.cards.trafficTrend && (
        <Box mb={{ base: 6, md: 8 }}>
          <TrafficTrendCard data={trafficData} loading={trafficLoading} dateRange={dateRange} />
        </Box>
      )}

      {/* Insights Row */}
      {(() => {
        const insightCards = ['quickInsights', 'deviceBreakdown', 'topReferrers'];
        const visibleInsights = insightCards.filter((k) => dashboardConfig.cards[k]);
        if (visibleInsights.length === 0) return null;
        const colCount = Math.min(visibleInsights.length, 3);
        return (
          <SimpleGrid
            columns={{ base: 1, lg: colCount }}
            spacing={{ base: 4, md: 6 }}
            mb={{ base: 6, md: 8 }}
          >
            {dashboardConfig.cards.quickInsights && (
              <QuickInsightsCard data={data} loading={loading} />
            )}
            {dashboardConfig.cards.deviceBreakdown && (
              <DeviceBreakdownCard data={devicesData} loading={devicesLoading} />
            )}
            {dashboardConfig.cards.topReferrers && (
              <TopReferrersCard data={sourcesData} loading={sourcesLoading} />
            )}
          </SimpleGrid>
        );
      })()}

      {/* Channel + Campaigns Row */}
      {(() => {
        const channelCards = ['channelPerformance', 'topCampaigns'];
        const visibleChannels = channelCards.filter((k) => dashboardConfig.cards[k]);
        if (visibleChannels.length === 0) return null;
        const colCount = Math.min(visibleChannels.length, 2);
        return (
          <SimpleGrid
            columns={{ base: 1, lg: colCount }}
            spacing={{ base: 4, md: 6 }}
            mb={{ base: 6, md: 8 }}
          >
            {dashboardConfig.cards.channelPerformance && (
              <ChannelPerformanceCard
                webImpressions={data?.adImpressions}
                webClicks={data?.adClicks}
                emailImpressions={0}
                emailClicks={0}
                loading={loading}
              />
            )}
            {dashboardConfig.cards.topCampaigns && <TopCampaignsCard dateRange={dateRange} />}
          </SimpleGrid>
        );
      })()}

      {/* Recent Activity - Full Width */}
      {dashboardConfig.cards.recentActivity && <RecentActivityCard />}

      {/* Dashboard Config Modal */}
      <DashboardConfigModal
        isOpen={isConfigOpen}
        onClose={onConfigClose}
        config={dashboardConfig}
        onConfigChange={setDashboardConfig}
      />

      {/* Export Builder Modal */}
      <ExportBuilderModal
        isOpen={isExportOpen}
        onClose={onExportClose}
        pageType="dashboard"
        data={{
          overview: data,
          trafficData,
          sourcesData,
          devicesData,
        }}
        dateRange={dateRange}
      />
    </Box>
  );
}

export default DashboardPage;
